/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { GridColDef, GridColumnVisibilityModel, GridFilterItem } from '@mui/x-data-grid'
import { View, Text, Icon, Checkbox } from '@components/atoms'
import { YearFilterPicker } from '@grid/components/YearFilterPicker'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

export type SidePanelTab = 'columns' | 'filters' | null

interface PropsType {
	apiRef: React.MutableRefObject<any>
	columns: GridColDef[]
	openTab: SidePanelTab
	onChangeTab: (tab: SidePanelTab) => void
	onReorder?: (fromField: string, toField: string) => void
}

const SKIP_FIELDS = new Set(['idx', 'actions', '__check__'])

/** 필터는 'YYYY-MM-DD' 문자열로 들고 있고, DatePicker 만 Date 를 쓴다. */
const toDate = (value?: string): Date | null =>
	value === undefined || value === '' ? null : dayjs(value).toDate()

const fromDate = (value: Date | null): string =>
	value === null || !dayjs(value).isValid() ? '' : dayjs(value).format('YYYY-MM-DD')

type FilterEntry = { value?: string; from?: string; to?: string }

type SelectOption = { value: any; label: string }

const resolveSelectOptions = (col: GridColDef): SelectOption[] => {
	const opts = (col as any).valueOptions
	if (!opts) return []
	const list = typeof opts === 'function' ? opts({ field: col.field }) : opts
	if (!Array.isArray(list)) return []
	return list.map(o =>
		typeof o === 'object' && o !== null
			? { value: o.value, label: String(o.label ?? o.value) }
			: { value: o, label: String(o) },
	)
}

export const GridSidePanel = (props: PropsType) => {
	const { apiRef, columns, openTab, onChangeTab, onReorder } = props

	// 토글 가능한(인덱스/액션 제외) 컬럼들
	const userColumns = React.useMemo(
		() => columns.filter(col => !SKIP_FIELDS.has(col.field)),
		[columns],
	)

	const [visibilityModel, setVisibilityModel] = React.useState<GridColumnVisibilityModel>(
		{},
	)
	const [searchKeyword, setSearchKeyword] = React.useState<string>('')
	const [filterValues, setFilterValues] = React.useState<Record<string, FilterEntry>>({})
	const [draggingField, setDraggingField] = React.useState<string | null>(null)
	const [dragOverField, setDragOverField] = React.useState<string | null>(null)

	const onColumnDragStart = (field: string) => (e: React.DragEvent) => {
		setDraggingField(field)
		e.dataTransfer.effectAllowed = 'move'
		e.dataTransfer.setData('text/plain', field)
	}

	const onColumnDragOver = (field: string) => (e: React.DragEvent) => {
		e.preventDefault()
		e.dataTransfer.dropEffect = 'move'
		if (field !== dragOverField) setDragOverField(field)
	}

	const onColumnDrop = (targetField: string) => (e: React.DragEvent) => {
		e.preventDefault()
		const fromField = draggingField
		setDraggingField(null)
		setDragOverField(null)
		if (!fromField || fromField === targetField) return
		onReorder?.(fromField, targetField)
	}

	const onColumnDragEnd = () => {
		setDraggingField(null)
		setDragOverField(null)
	}

	// apiRef 외부 변경 동기화 (컬럼 헤더 메뉴에서 hide 등)
	React.useEffect(() => {
		const api = apiRef.current
		if (!api?.subscribeEvent) return
		const unsubscribe = api.subscribeEvent(
			'columnVisibilityModelChange',
			(model: GridColumnVisibilityModel) => setVisibilityModel({ ...model }),
		)
		return () => {
			if (typeof unsubscribe === 'function') unsubscribe()
		}
	}, [apiRef])

	const isVisible = (field: string) => visibilityModel[field] !== false

	const toggleColumn = (field: string) => {
		const newModel: GridColumnVisibilityModel = {
			...visibilityModel,
			[field]: !isVisible(field),
		}
		setVisibilityModel(newModel)
		apiRef.current?.setColumnVisibilityModel(newModel)
	}

	const allChecked = userColumns.every(col => isVisible(col.field))
	const someChecked = userColumns.some(col => isVisible(col.field))

	const toggleAll = () => {
		const nextVisible = !allChecked
		const newModel: GridColumnVisibilityModel = { ...visibilityModel }
		userColumns.forEach(col => {
			newModel[col.field] = nextVisible
		})
		setVisibilityModel(newModel)
		apiRef.current?.setColumnVisibilityModel(newModel)
	}

	const filteredColumns = React.useMemo(() => {
		const kw = searchKeyword.trim().toLowerCase()
		if (!kw) return userColumns
		return userColumns.filter(col =>
			String(col.headerName ?? col.field)
				.toLowerCase()
				.includes(kw),
		)
	}, [userColumns, searchKeyword])

	const applyFilterModel = (next: Record<string, FilterEntry>) => {
		const items: GridFilterItem[] = []
		Object.entries(next).forEach(([f, entry]) => {
			const col = columns.find(c => c.field === f)
			const type = col?.type
			const pickerType = (col as any)?.pickerType

			if (pickerType === 'year') {
				if (entry.value) {
					const y = entry.value
					items.push({
						field: f,
						operator: 'between',
						value: { from: `${y}-01-01`, to: `${y}-12-31` },
					})
				}
				return
			}

			if (type === 'date' || type === 'dateTime') {
				if (entry.from || entry.to) {
					items.push({
						field: f,
						operator: 'between',
						value: { from: entry.from, to: entry.to },
					})
				}
			} else if (type === 'boolean') {
				if (entry.value === 'true' || entry.value === 'false') {
					items.push({ field: f, operator: 'is', value: entry.value })
				}
			} else if (type === 'singleSelect') {
				if (entry.value !== undefined && entry.value !== '') {
					items.push({ field: f, operator: 'is', value: entry.value })
				}
			} else if (type === 'number') {
				if (entry.value !== undefined && entry.value !== '') {
					items.push({ field: f, operator: '=', value: entry.value })
				}
			} else {
				if (entry.value !== undefined && entry.value !== '') {
					items.push({ field: f, operator: 'contains', value: entry.value })
				}
			}
		})

		apiRef.current?.setFilterModel({ items, logicOperator: 'and' })
	}

	const onFilterPatch = (field: string, patch: Partial<FilterEntry>) => {
		const newValues = {
			...filterValues,
			[field]: { ...(filterValues[field] ?? {}), ...patch },
		}
		setFilterValues(newValues)
		applyFilterModel(newValues)
	}

	const filterableColumns = userColumns.filter(col => col.filterable !== false)

	return (
		<View css={style}>
			{openTab !== null && (
				<View className="panel">
					{openTab === 'columns' && (
						<View className="panelInner">
							<View className="panelHeader">
								<Text category="s2">컬럼</Text>
							</View>
							<View className="panelBody">
								<input
									className="searchInput"
									placeholder="검색..."
									value={searchKeyword}
									onChange={e => setSearchKeyword(e.target.value)}
								/>
								<View className="toggleAllRow">
									<Checkbox
										size="small"
										checked={allChecked}
										indeterminate={!allChecked && someChecked}
										onChange={toggleAll}
									/>
									<Text category="p2">모든 컬럼</Text>
								</View>
								<View className="columnList">
									{filteredColumns.map(col => (
										<View
											key={col.field}
											className={`columnRow${
												draggingField === col.field ? ' dragging' : ''
											}${dragOverField === col.field ? ' dragOver' : ''}`}
											draggable
											onDragStart={onColumnDragStart(col.field)}
											onDragOver={onColumnDragOver(col.field)}
											onDrop={onColumnDrop(col.field)}
											onDragEnd={onColumnDragEnd}
										>
											<span className="mdi mdi-drag-vertical dragHandle" aria-hidden />
											<Checkbox
												size="small"
												checked={isVisible(col.field)}
												onChange={() => toggleColumn(col.field)}
											/>
											<Text category="p2">{String(col.headerName ?? col.field)}</Text>
										</View>
									))}
									{filteredColumns.length === 0 && (
										<View className="emptyRow">
											<Text category="c1" hint="500">
												검색 결과 없음
											</Text>
										</View>
									)}
								</View>
							</View>
						</View>
					)}

					{openTab === 'filters' && (
						<View className="panelInner">
							<View className="panelHeader">
								<Text category="s2">필터</Text>
							</View>
							<View className="panelBody">
								<View className="filterList">
									{filterableColumns.map(col => {
										const entry = filterValues[col.field] ?? {}
										const type = col.type
										const pickerType = (col as any)?.pickerType
										return (
											<View key={col.field} className="filterRow">
												<Text category="c1" hint="700">
													{String(col.headerName ?? col.field)}
												</Text>
												{pickerType === 'year' ? (
													<YearFilterPicker
														className="filterSelect"
														value={entry.value}
														onChange={next => onFilterPatch(col.field, { value: next })}
													/>
												) : type === 'boolean' ? (
													<select
														className="filterSelect"
														value={entry.value ?? ''}
														onChange={e =>
															onFilterPatch(col.field, { value: e.target.value })
														}
													>
														<option value="">전체</option>
														<option value="true">예</option>
														<option value="false">아니오</option>
													</select>
												) : type === 'singleSelect' ? (
													<select
														className="filterSelect"
														value={entry.value ?? ''}
														onChange={e =>
															onFilterPatch(col.field, { value: e.target.value })
														}
													>
														<option value="">전체</option>
														{resolveSelectOptions(col).map(opt => (
															<option key={String(opt.value)} value={String(opt.value)}>
																{opt.label}
															</option>
														))}
													</select>
												) : type === 'number' ? (
													<input
														className="filterInput"
														type="number"
														placeholder="값 입력..."
														value={entry.value ?? ''}
														onChange={e =>
															onFilterPatch(col.field, { value: e.target.value })
														}
													/>
												) : type === 'date' || type === 'dateTime' ? (
													/*
														네이티브 `<input type="date">` 는 브라우저 로케일을 따라
														mm/dd/yyyy 로 나온다. 표기를 코드로 못 바꾸므로 DatePicker 를 쓴다.
													*/
													<View className="dateRangeRow">
														<DatePicker
															value={toDate(entry.from)}
															onChange={v =>
																onFilterPatch(col.field, { from: fromDate(v) })
															}
															format="yyyy-MM-dd"
															slotProps={{
																textField: {
																	size: 'small',
																	className: 'filterDateField',
																},
															}}
														/>
														<span className="dateRangeSep">~</span>
														<DatePicker
															value={toDate(entry.to)}
															onChange={v =>
																onFilterPatch(col.field, { to: fromDate(v) })
															}
															format="yyyy-MM-dd"
															slotProps={{
																textField: {
																	size: 'small',
																	className: 'filterDateField',
																},
															}}
														/>
													</View>
												) : (
													<input
														className="filterInput"
														type="text"
														placeholder="값 입력..."
														value={entry.value ?? ''}
														onChange={e =>
															onFilterPatch(col.field, { value: e.target.value })
														}
													/>
												)}
											</View>
										)
									})}
								</View>
							</View>
						</View>
					)}
				</View>
			)}

			<View className="tabBar">
				<View
					className={`tabItem${openTab === 'columns' ? ' active' : ''}`}
					onClick={() => onChangeTab(openTab === 'columns' ? null : 'columns')}
				>
					<Icon name="view-column" />
					<span className="tabLabel">컬럼</span>
				</View>
				<View
					className={`tabItem${openTab === 'filters' ? ' active' : ''}`}
					onClick={() => onChangeTab(openTab === 'filters' ? null : 'filters')}
				>
					<Icon name="filter" />
					<span className="tabLabel">필터</span>
				</View>
			</View>
		</View>
	)
}

const style = css({
	display: 'flex',
	// 높이는 퍼센트가 아니라 stretch 로 받는다 — 페이지가 높이 체인을 안 주면 `height: 100%` 는 auto 가 되면서
	// stretch 도 안 걸려(값이 auto 가 아니라서) 좌측 경계선이 탭 바 높이에서 끊겼다 (쿠폰·고객·박스, 2026-09-27)
	alignSelf: 'stretch',
	minHeight: 0,
	flexShrink: 0,
	borderLeft: `1px solid ${mono.line}`,
	boxSizing: 'border-box',

	'.panel': {
		width: '260px',
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: mono.surface,
		borderRight: `1px solid ${mono.line}`,
		overflow: 'hidden',
		boxSizing: 'border-box',
	},

	'.panelInner': {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 0,
	},

	'.panelHeader': {
		padding: '0.6rem 0.75rem',
		borderBottom: `1px solid ${mono.line}`,
		backgroundColor: mono.surface,
	},

	'.panelBody': {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		padding: '0.5rem 0.5rem 0.75rem',
	},

	'.searchInput, .filterInput, .filterSelect': {
		width: '100%',
		padding: '0.35rem 0.5rem',
		border: 0,
		borderBottom: `1px solid ${mono.lineInput}`,
		borderRadius: 0,
		fontFamily: boFont.ui,
		fontSize: 12,
		fontWeight: 300,
		color: mono.ink,
		outline: 'none',
		boxSizing: 'border-box',
		backgroundColor: mono.surface,
		'&:focus': {
			borderBottomColor: mono.ink,
			boxShadow: 'none',
		},
	},

	'.filterSelect': {
		appearance: 'auto',
		cursor: 'pointer',
	},

	'.dateRangeRow': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.25rem',
		'& > .filterInput': {
			flex: 1,
			minWidth: 0,
		},
		// 사이드 패널이 좁다 — DatePicker 가 칸을 넘지 않게 줄인다
		'.filterDateField': {
			flex: 1,
			minWidth: 0,
			'.MuiInputBase-input': {
				fontSize: '0.8rem',
				padding: '0.35rem 0.5rem',
			},
			'.MuiInputAdornment-root': {
				marginLeft: 0,
			},
			'.MuiIconButton-root': {
				padding: '0.125rem',
			},
		},
	},

	'.dateRangeSep': {
		color: mono.faint,
		fontSize: '0.8rem',
	},

	'.toggleAllRow': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.25rem',
		padding: '0.35rem 0 0.25rem',
		marginTop: '0.25rem',
		borderBottom: `1px solid ${mono.line}`,
	},

	'.columnList': {
		display: 'flex',
		flexDirection: 'column',
		paddingTop: '0.25rem',
	},

	'.columnRow': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.15rem',
		padding: '0.1rem 0.15rem',
		borderRadius: 2,
		'&:hover': { backgroundColor: mono.bg },
		'&.dragging': { opacity: 0.4 },
		'&.dragOver': {
			backgroundColor: mono.bg,
			boxShadow: `inset 0 -1px 0 ${mono.ink}`,
		},
	},

	'.dragHandle': {
		cursor: 'grab',
		color: mono.faint,
		fontSize: 16,
		'&:active': { cursor: 'grabbing' },
	},

	'.emptyRow': {
		padding: '0.75rem 0.25rem',
		textAlign: 'center',
	},

	'.filterList': {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.6rem',
	},

	'.filterRow': {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.2rem',
	},

	'.tabBar': {
		width: '36px',
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: mono.surface,
		borderLeft: `1px solid ${mono.line}`,
		flexShrink: 0,
	},

	'.tabItem': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: '0.35rem',
		padding: '0.6rem 0',
		cursor: 'pointer',
		color: mono.dim,
		userSelect: 'none',
		borderBottom: `1px solid ${mono.line}`,
		transition: 'color 200ms ease-out, background-color 200ms ease-out',
		'&:hover': { backgroundColor: mono.bg, color: mono.ink },
		'&.active': {
			backgroundColor: mono.surface,
			color: mono.ink,
			boxShadow: `inset 1px 0 0 ${mono.ink}`,
		},
	},

	'.tabLabel': {
		writingMode: 'vertical-rl',
		fontFamily: boFont.ui,
		fontSize: 11,
		fontWeight: 500,
		letterSpacing: '.16em',
		textTransform: 'uppercase',
	},
})
